/**
 * requireCapability.ts
 *
 * Microkernel authorization guard enforcing zero client trust.
 * Evaluates requested capabilities and unconditionally blocks destructive
 * operations in role-preview simulation mode.
 */

import type { AuthorizationContext, Capability } from './capabilities.js';

export class AuthorizationError extends Error {
  constructor(
    public readonly code: 'E_UNAUTHORIZED_CAPABILITY' | 'E_PREVIEW_MODE_READONLY' | 'E_UNAUTHENTICATED',
    message: string,
    public readonly details?: Record<string, unknown>
  ) {
    super(message);
    this.name = 'AuthorizationError';
  }
}

const READ_ONLY_CAPABILITIES: ReadonlySet<Capability> = new Set([
  'inventory.read',
]);

/**
 * Checks if the context satisfies the required capability without throwing.
 */
export function checkCapability(
  capability: Capability,
  context: AuthorizationContext
): boolean {
  if (!context.principal.authenticated) {
    return false;
  }

  // In role preview mode, only read capabilities are permitted
  if (context.sessionMode.type === 'role-preview') {
    if (!READ_ONLY_CAPABILITIES.has(capability)) {
      return false;
    }
  }

  return context.capabilities.includes(capability);
}

/**
 * Authoritatively asserts capability possession. Throws AuthorizationError on violation.
 */
export function requireCapability(
  capability: Capability,
  context: AuthorizationContext,
  resourceDetails?: Record<string, unknown>
): void {
  if (!context.principal.authenticated) {
    throw new AuthorizationError(
      'E_UNAUTHENTICATED',
      `Principal ${context.principal.id} is not authenticated`,
      { principalId: context.principal.id, capability }
    );
  }

  if (context.sessionMode.type === 'role-preview' && !READ_ONLY_CAPABILITIES.has(capability)) {
    throw new AuthorizationError(
      'E_PREVIEW_MODE_READONLY',
      `Action '${capability}' blocked: Role preview mode (${context.sessionMode.previewRole}) is strictly read-only`,
      { previewRole: context.sessionMode.previewRole, capability, ...resourceDetails }
    );
  }

  if (!context.capabilities.includes(capability)) {
    throw new AuthorizationError(
      'E_UNAUTHORIZED_CAPABILITY',
      `Principal ${context.principal.id} lacks required capability '${capability}'`,
      { principalId: context.principal.id, capability, roles: context.roles, ...resourceDetails }
    );
  }
}
